"use client";
import { faSignOutAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Icon } from "@iconify/react";
import { MenuItem, Select } from "@material-ui/core";
import {
  AccountCircle,
  ArrowBack,
  ArrowForward,
  Call,
  ChevronLeft,
  ChevronRight,
  ClearAll,
  Language,
  Person,
  Payment,
} from "@material-ui/icons";
import Link from "components/Link";
import { pagePadding } from "data/commonClasses";
import { newsLinks, oeaLinks, technicalOfficeLinks } from "data/menuItems";
import { fetchFromAPI, setCookie } from "functions";
import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { twMerge } from "tailwind-merge";
import useTranslation from "translations/hooks/useTranslation";
import Button from "./Button";
import SessionContext from "./session/SessionContext";
import Text from "./Text";

export default function Header() {
  const router = useRouter();
  const { t } = useTranslation();
  let mobileWidth = 1024;
  let mobileDimensions = { query: "(max-width: 1024px)" };
  let isMobile = useMediaQuery(mobileDimensions);
  const [hydrated, setHydrated] = useState(false);
  let {
    actions: { signout, setSession },
    session: { user, lang },
  } = useContext(SessionContext);

  let isLoggedIn = user && user?.authToken && user?._id;
  const [isOpen, setIsOpen] = useState(true);
  const [language, setLanguage] = useState(lang);
  const [navbarBg, setNavbarBg] = useState("");

  useEffect(() => {
    setHydrated(true);
  }, []);

  useEffect(() => {
    window.innerWidth > mobileWidth ? setIsOpen(true) : setIsOpen(false);

    window.addEventListener("resize", (e) => {
      window.innerWidth > mobileWidth ? setIsOpen(true) : setIsOpen(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getNavbarBg = async () => {
    let requests = [
      {
        key: "navbarBackground",
        apiName: "oea-pages-content",
        reqBody: { key: "navbar-background", lang },
      },
    ];
    let response = await fetchFromAPI({ api: "home", reqBody: requests });
    setNavbarBg(response?.navbarBackground?.response);
    // console.log({getNavbarBg:response});
    // return response?.navbarBackground?.response
  };

  useEffect(() => {
    getNavbarBg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleLanguage(value) {
    setCookie("lang", value, 30);
    setLanguage(value);
    setSession({ lang: value });
    let {
      asPath,
      query: { lang = "en" },
    } = router;
    let path = asPath.replace(lang, value);
    if (typeof location !== "undefined" && location)
      location.href = asPath == "/" ? `/${value}` : path;
  }

  function handleClose() {
    if (window.innerWidth > mobileWidth) return;
    setIsOpen(false);
  }

  function ProfilePicture() {
    let userCode = user?.emails?.length ? user?.emails?.[0]?.address : "";
    return (
      <Link href="/userProfile" as="/userProfile">
        <Button
          mainClass="mt-0"
          IconComp={AccountCircle}
          title={user?.profile?.firstName || userCode}
        />
      </Link>
    );
  }

  const renderMenuLink = ({ links, title, i }) => {
    const dropdownClass =
      lang == "ar"
        ? `absolute top-[70%] mt-[15px] bg-white min-w-[200px] max-w-[200px] py-2 z-[9999] rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.15)] hidden group-hover:block text-right dropdown${i}`
        : `absolute top-[70%] mt-[15px] bg-white min-w-[200px] max-w-[200px] py-2 z-[9999] rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.15)] hidden group-hover:block text-left dropdown${i}`;
    return (
      <div className="flex items-center h-full min-h-[40px] justify-center relative px-2 group">
        <div className="flex flex-row items-center cursor-pointer gap-2 h-full">
          <a className="relative flex items-center min-h-[40px] px-2 text-[#344450] text-[14px] transition-all duration-200 ease-in-out hover:text-[#F29F05] focus:outline-none border-none bg-inherit enhanced-hover">
            <span className="absolute inset-0 -top-2 -bottom-2 -left-3 -right-3 z-0 bg-transparent pointer-events-auto"></span>
            <span className="relative z-10 w-full flex items-center">
              <Text text={t(title)} />
            </span>
          </a>
          <Icon icon="bxs:down-arrow" className="text-black" />
        </div>
        <div
          className={twMerge(`${dropdownClass} z-30`)}
          style={{ textAlign: lang == "ar" ? "right" : "left" }}
        >
          {links?.map((oneLink, index) => {
            // Check if this is the current URL to prevent navigation
            const linkPath = `/${lang}${oneLink?.as || oneLink?.href}`;
            const isCurrentPath =
              typeof window !== "undefined" &&
              window.location.pathname.replace(/\/$/, "") ===
                linkPath.replace(/\/$/, "");

            return (
              <Link key={index} href={oneLink?.href} as={oneLink?.as}>
                <a
                  className="flex w-full items-center text-black no-underline px-4 py-1.5 rounded-md hover:bg-[#f5b750] hover:text-white transition-colors duration-200 text-sm mx-2 my-1"
                  style={{
                    boxSizing: "border-box",
                    maxWidth: "calc(100% - 1rem)",
                  }}
                  onClick={(e) => {
                    if (isCurrentPath) {
                      // Prevent navigation if already on this page
                      e.preventDefault();
                    }
                    handleClose();
                  }}
                >
                  <Text text={t(oneLink?.title)} className="w-full" />
                </a>
              </Link>
            );
          })}
        </div>
      </div>
    );
  };

  const renderLink = ({ title, href, as, onlyLoggedIn = false }) => {
    // Check if this is the current URL to prevent navigation
    const linkPath = `/${lang}${as || href}`;
    const isCurrentPath =
      typeof window !== "undefined" &&
      window.location.pathname.replace(/\/$/, "") ===
        linkPath.replace(/\/$/, "");

    return (isLoggedIn && onlyLoggedIn) || !onlyLoggedIn ? (
      <div className="flex items-center h-full min-h-[40px] justify-center relative px-2 group">
        <Link href={href} as={as}>
          <a
            className="flex items-center h-full min-h-[40px] justify-center rounded-lg px-4 py-2 mx-1 text-[#344450] text-[14px] transition-all duration-200 ease-in-out hover:bg-[#f29f051a] focus:outline-none border-none bg-transparent"
            onClick={(e) => {
              if (isCurrentPath) {
                // Prevent navigation if already on this page
                e.preventDefault();
              }
              handleClose();
            }}
          >
            <Text text={t(title)} className="w-full flex items-center" />
          </a>
        </Link>
      </div>
    ) : null;
  };

  return (
    <div className="mb-5 relative">
      {navbarBg?.image && (
        <img
          src={navbarBg?.image}
          width={300}
          height={300}
          alt="navbarBg"
          className={twMerge(
            "h-[4.5rem] w-20 object-cover aspect-square absolute opacity-10 md:opacity-100 z-[1] bottom-0",
            lang == "ar" ? "left-0" : "right-0 scale-x-[-1]"
          )}
        />
      )}
      <div
        className={twMerge(
          "bg-oeaMain flex flex-row justify-between items-center py-4 min-h-[80px] relative z-10",
          pagePadding
        )}
      >
        <div className="flex flex-row items-center justify-between w-full">
          {/* Left side - Hamburger menu when mobile */}
          {hydrated && !isMobile ? null : !isOpen ? (
            <div className="flex flex-row items-center text-white">
              <label htmlFor="my-drawer-4">
                <Icon
                  icon={"mingcute:menu-fill"}
                  className="cursor-pointer"
                  width={32}
                  height={32}
                  color="#FFF"
                />
              </label>
            </div>
          ) : null}

          {/* Center - Language switcher when not mobile */}
          {hydrated && !isMobile && (
            <div className="flex flex-row items-center justify-center bg-white rounded-btn h-10">
              <Select
                IconComponent={() => null}
                className={`h-full py-0`}
                style={{ color: "#F29F05" }}
                value={language || "en"}
                label="Language"
                onChange={(e) => handleLanguage(e?.target?.value)}
                disableUnderline
              >
                <MenuItem value="en" className="hover:bg-orange">
                  <Text bold text={"English"} />
                </MenuItem>
                <MenuItem value="fr">
                  <Text bold text={"Français"} />
                </MenuItem>
                <MenuItem value="ar">
                  <Text bold text={"العربية"} />
                </MenuItem>
              </Select>
              <Language className="text-orange mx-1" />
            </div>
          )}

          {/* Right side - Actions and profile */}
          <div className="flex flex-row items-center gap-4">
            {/* Language switcher when mobile */}
            {hydrated && isMobile && (
              <div className="flex flex-row items-center justify-center bg-white rounded-btn h-10">
                <Select
                  IconComponent={() => null}
                  className={`h-full py-0`}
                  style={{ color: "#F29F05" }}
                  value={language || "en"}
                  label="Language"
                  onChange={(e) => handleLanguage(e?.target?.value)}
                  disableUnderline
                >
                  <MenuItem value="en" className="hover:bg-orange">
                    <Text bold text={"English"} />
                  </MenuItem>
                  <MenuItem value="fr">
                    <Text bold text={"Français"} />
                  </MenuItem>
                  <MenuItem value="ar">
                    <Text bold text={"العربية"} />
                  </MenuItem>
                </Select>
                <Language className="text-orange mx-1" />
              </div>
            )}

            {/* {hydrated &&
              (isMobile || !isLoggedIn ? null : (
                <Button
                  linkProps={{
                    href: `/${lang}/online-payment`,
                    as: `/${lang}/online-payment`,
                  }}
                  whiteBg
                  IconComp={Payment}
                  title={t("payOnline")}
                  mainClass="mt-0"
                  className="bg-white hover:bg-gray-50"
                />
              ))} */}
            {hydrated &&
              (isMobile ? null : (
                <Button
                  linkProps={{
                    href: `/${lang}/contactus`,
                    as: `/${lang}/contactus`,
                  }}
                  IconComp={Call}
                  title={t("contactUs")}
                  mainClass="mt-0"
                  className="bg-orange hover:bg-orange/90"
                />
              ))}

            {hydrated &&
              (user && user?.authToken && user?._id ? (
                <div className="ml-0">
                  <ProfilePicture />
                </div>
              ) : !isMobile ? (
                <Button
                  mainClass="mt-0"
                  linkProps={{
                    href: `/${lang}/signin`,
                    as: `/${lang}/signin`,
                  }}
                  IconComp={Person}
                  title={t("signIn")}
                  className="bg-orange hover:bg-orange/90"
                />
              ) : null)}

            {hydrated &&
              (isMobile && !isLoggedIn ? (
                <div className="bg-orange rounded-btn p-2 hover:bg-orange/90">
                  <Link href={`/signin`} as={`/signin`}>
                    <a>
                      <Person className="text-white" />
                    </a>
                  </Link>
                </div>
              ) : null)}

            {hydrated &&
              (isMobile || !isLoggedIn ? null : (
                <Button
                  onClick={signout}
                  whiteBg
                  iconName={"basil:logout-solid"}
                  title={""}
                  mainClass="mt-0"
                  iconClassName={twMerge(
                    "mx-0",
                    lang !== "ar" && "scale-x-[-1]"
                  )}
                  className="bg-white hover:bg-gray-50 p-2"
                />
              ))}
          </div>
        </div>
      </div>

      <div
        className={twMerge(
          `border-b-2 z-20 relative border-[#E5E8EB] flex overflow-visible gap-5 w-full items-center min-h-[80px]`,
          pagePadding
        )}
      >
        <Link href="/" as="/" className="z-10">
          <div className={"flex flex-row items-center"}>
            <img
              src="/logo.png"
              alt="OEA Logo"
              width={100}
              height={100}
              className="w-14 h-14 cursor-pointer"
            />
            <p
              dangerouslySetInnerHTML={{ __html: t("homeTitle") }}
              className={
                "w-full mx-4 font-bold text-input text-black max-h-14 min-w-[150px] overflow-hidden"
              }
            />
          </div>
        </Link>

        {!isOpen ? null : (
          <div className="flex-1 flex z-20 justify-center items-center relative overflow-visible">
            {isMobile ? null : (
              <div className="flex flex-row items-center justify-center gap-4 h-full overflow-visible">
                {renderMenuLink({
                  i: 1,
                  title: "oea",
                  links: oeaLinks,
                })}
                {renderLink({
                  title: "financialAndMedicalTransactions",
                  href: "/financialMedicalTransactions",
                  as: "/financialMedicalTransactions",
                })}
                {renderMenuLink({
                  i: 2,
                  title: "technicalOffice",
                  links: technicalOfficeLinks,
                })}
                {renderLink({
                  title: "arbitrationCenter",
                  href: "/arbitration",
                })}
                {renderLink({
                  href: "/trainings/1",
                  as: "/trainings/1",
                  title: "trainingCenter",
                })}
                {renderMenuLink({
                  i: 3,
                  title: "news",
                  links: newsLinks,
                })}
                {/* {renderLink({
                  onlyLoggedIn: true,
                  title: "eorder",
                  href: "/eorder",
                  as: "/eorder",
                })} */}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
